import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import type { DataTablePageEvent } from 'primereact/datatable';
import NADWaiverTable from '../OverviewTab/components/FormularySection/NADWaiverTable';
import LabelerInformationTable from './components/LabelerInformationTable';
import NADWaiverSectionSkeleton from './components/NADWaiverSectionSkeleton/NADWaiverSectionSkeleton';
import NADWaiverSectionMessage from './components/NADWaiverSectionMessage/NADWaiverSectionMessage';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import './ManufacturerTab.styles.scss';

/**
 * Manufacturer Tab
 * Shows:
 * - NAD Waiver (grouped by manufacturer)
 * - Labeler Information (coming next)
 * - Drug Establishment (FEID) - wireframes expected
 * - Federal Schedules - wireframes expected
 * - Cross Ruling - wireframes expected
 */

const PAGE_SIZE = 10;
const NAD_WAIVER_SEARCH_DEBOUNCE_MS = 350;

const ManufacturerTab: React.FC = () => {
  const {
    nadWaiversData,
    isNadWaiversLoading,
    nadWaiversError,
    currentGfc,
    fetchNadWaivers,
  } = productAnalysisStore;

  const [searchTerm, setSearchTerm] = useState('');
  const nadWaiverSearchFirstRunRef = useRef(true);

  useEffect(() => {
    const gfc = currentGfc?.trim();
    if (!gfc) return;
    if (nadWaiverSearchFirstRunRef.current) {
      nadWaiverSearchFirstRunRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchNadWaivers({
        gfc,
        page: 1,
        page_size: PAGE_SIZE,
        search: searchTerm.trim() || undefined,
      });
    }, NAD_WAIVER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentGfc]);

  const handleNadWaiverPageChange = (event: DataTablePageEvent) => {
    const gfc = currentGfc?.trim();
    if (!gfc) return;
    const page = Math.floor(event.first / event.rows) + 1;
    fetchNadWaivers({
      gfc,
      page,
      page_size: event.rows,
      search: searchTerm.trim() || undefined,
    });
  };

  const renderNadWaiverSection = () => {
    if (isNadWaiversLoading && !nadWaiversData) {
      return <NADWaiverSectionSkeleton />;
    }
    if (nadWaiversError && !nadWaiversData) {
      return (
        <NADWaiverSectionMessage
          type="error"
          message={nadWaiversError}
        />
      );
    }
    // Show table (with search bar) whenever we have API response, including empty results
    if (nadWaiversData) {
      const totalGroups = nadWaiversData.total_groups ?? 0;
      return (
        <NADWaiverTable
          waivers={nadWaiversData}
          totalRecords={totalGroups}
          loading={isNadWaiversLoading}
          onPageChange={handleNadWaiverPageChange}
          pageSize={PAGE_SIZE}
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
        />
      );
    }
    // Not yet loaded (no response yet)
    return (
      <NADWaiverSectionMessage
        type="empty"
        message="No active NAD waivers."
      />
    );
  };

  return (
    <div className="manufacturer-tab">
      {/* NAD Waiver section: always render section shell so skeleton and empty state are visible */}
      <section className="manufacturer-tab__nad-waiver-section">
        {renderNadWaiverSection()}
      </section>

      {/* Labeler Information Table */}
      <LabelerInformationTable />
    </div>
  );
};

export default observer(ManufacturerTab);
